import { ExpandMore, FileDownload } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
// Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  FormLabel,
  Grid
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC } from 'react';
import { ISiteReport } from '../../../models';

interface IMobileSiteReportProps {
  original: ISiteReport;
  fetchSitePrepReport: (val: ISiteReport) => void;
  isDownloadingSitePrepReport: {
    [id: string | number]: boolean;
  };
  fetchSitePlanningReport: (val: ISiteReport) => void;
  isDownloadingSitePlanningReport: {
    [id: string | number]: boolean;
  };
}

export const MobileSiteReports: FC<IMobileSiteReportProps> = ({
  original,
  fetchSitePrepReport,
  isDownloadingSitePrepReport,
  fetchSitePlanningReport,
  isDownloadingSitePlanningReport
}) => {
  const classes = useStyles();
  const { jobId, code, neighborhoodDescription, lotNumber, jobStatus, jobType } = original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls={`${jobId}-content`}
        id={`${jobId}-header`}
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">
            {neighborhoodDescription} - Lot {lotNumber}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails
        className={classes.details}
        id={`${jobId}-content`}
        aria-labelledby={`${jobId}-header`}
      >
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Neighborhood:
          </FormLabel>
          <Typography component="span" variant="body2">
            {neighborhoodDescription || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Lot:
          </FormLabel>
          <Typography component="span" variant="body2">
            {lotNumber || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Job ID:
          </FormLabel>
          <Typography component="span" variant="body2">
            {code || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Status:
          </FormLabel>
          <Typography component="span" variant="body2">
            {jobStatus || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Type:
          </FormLabel>
          <Typography component="span" variant="body2">
            {jobType || '--'}
          </Typography>
        </div>
        <Grid container spacing={1}>
          <Grid item>
            <LoadingButton
              id={`site-prep-${jobId}`}
              loading={isDownloadingSitePrepReport[original.jobId]}
              loadingPosition="start"
              color="primary"
              variant="contained"
              title="Download Site Prep Report"
              startIcon={<FileDownload />}
              onClick={() => fetchSitePrepReport(original)}
            >
              Site Prep Report
            </LoadingButton>
          </Grid>
          <Grid item>
            <LoadingButton
              id={`site-planning-${jobId}`}
              loading={isDownloadingSitePlanningReport[original.jobId]}
              loadingPosition="start"
              color="primary"
              variant="contained"
              title="Download Site Planning Report"
              startIcon={<FileDownload />}
              onClick={() => fetchSitePlanningReport(original)}
            >
              Site Planning Report
            </LoadingButton>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    marginBottom: '0.5rem',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  editButton: {
    padding: `2px 5px`
  }
}));
